import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { ExtendedFAB, Icon, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { ONE_OFF_EVENT_TYPES, translateEventType } from '../../entities/events/shared';
import { isDependant } from '../../entities/recipients/shared';
import { TEventTypes, useUnskipEventMutation } from '../../generated/graphql';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { getReadableRelationshipType } from '../../entities/dependants/shared';
import { TEventType, useSkippedUpcomingEventsOLD } from '../../entities/events/queries';
import { useEventsFilters } from './_shared';

export const SkippedEvents = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const tableRef = createRef<TTablePageRef>();

  const [includingLastDays, setIncludingLastDays] = useState<number>(60);

  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [unskipLoading, setUnskipLoading] = useState<boolean>(false);

  const { data: events, error, loading } = useSkippedUpcomingEventsOLD({ includingLastDays });

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    filterByRecipientName
  } = useEventsFilters({ events });

  const [unskipEvent] = useUnskipEventMutation();

  const unskipEvents = useCallback(async () => {
    setUnskipLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const event = events.find((item) => item.id === id);
        if (!event) continue;
        recordId = event.id;
        await unskipEvent({
          variables: { id: event.id, companyId: event.companyId, version: event._version }
        });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Event unskip failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error unskipping the selected events.');
    } finally {
      setUnskipLoading(false);
    }
  }, [selectedRows, events, tableRef, unskipEvent]);

  const onUnskipEventsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Unskip ${selectedRows?.length} Events`,
      supportingText: `Are you sure you want to unskip these ${selectedRows?.length} events?`,
      confirmLabel: 'Unskip',
      onConfirmClick: unskipEvents,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, tableRef, tableRef.current, unskipEvents]);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((event: TEventType) => {
    return event.id;
  }, []);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Date',
      name: 'date',
      onChange: setMonth,
      options: months.map((m) => ({ label: m, value: m })),
      value: monthFilter
    });

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: setCompany,
      options: companies?.map((c) => ({ label: `${c.name} (${companiesCounts[c.id]})`, value: c.id })),
      value: companyFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient Name',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientNameFilter(v != null ? (v as string) : undefined);
      },
      value: recipientNameFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Event Type',
      name: 'eventType',
      onChange: (v) => {
        setEventTypeFilter(v != null ? (v as TEventTypes) : undefined);
      },
      options: [
        {
          label: 'Birthday',
          value: TEventTypes.Birthday
        },
        {
          label: 'Work Anniversary',
          value: TEventTypes.WorkAnniversary
        },
        {
          label: 'New Hire',
          value: TEventTypes.NewHire
        },
        ...Object.entries(ONE_OFF_EVENT_TYPES).map(([key, value]) => ({ label: value, value: key }))
      ],
      value: eventTypeFilter
    });

    filtersList.push({
      includeEmptyOption: false,
      type: 'singleSelect',
      label: 'Including Last Days',
      name: 'includingLastDays',
      onChange: (v) => {
        setIncludingLastDays(v != null ? Number(v) : 60);
      },
      options: [
        {
          label: '60 Days',
          value: 60
        },
        {
          label: '90 Days',
          value: 90
        },
        {
          label: '120 Days',
          value: 120
        },
        {
          label: '150 Days',
          value: 150
        },
        {
          label: 'All Days',
          value: 999
        }
      ],
      value: includingLastDays
    });

    return filtersList;
  }, [companies, companiesCounts, setCompany, companyFilter, eventTypeFilter, setEventTypeFilter, monthFilter, months, setMonth, setRecipientNameFilter, recipientNameFilter, includingLastDays]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      return filterByCompany(e) && filterByMonth(e) && filterByEventType(e) && filterByRecipientName(e);
    });
  }, [filterByCompany, filterByEventType, filterByMonth, filterByRecipientName, events]);

  //console.log('events?:', events);
  console.log('filteredEvents?:', filteredEvents);

  return (
    <TablePage dataLoading={loading} actionsLoading={unskipLoading} error={error} ref={tableRef} title='Skipped Events'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TEventType>
          enableSorting={true}
          defaultSorting={{ id: 'eventDate', desc: false }}
          data={filteredEvents}
          emptyText={events.length === 0 ? 'No skipped events.' : 'No skipped events for the current filters.'}
          expandable={true}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={!unskipLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{cell.row.original.company?.name ?? '-'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName',
              cell: ({ cell }) => {
                let text = '';
                const pausePeriod = cell.row.original.employee?.pausePeriod;
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.recipient?.fullName}</span>
                  </div>
                );
              }
            },
            /* {
            header: 'Shipping Date',
            accessorKey: 'shippingDate',
            cell: ({ cell }) => {
              return (
                <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                  <span>{readableDate(cell.row.original.shippingDate ?? undefined)}</span>
                </div>
              );
            },
          }, */
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{translateEventType(cell.row.original.type)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.eventDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Relationship',
              accessorKey: 'recipient.type',
              cell: ({ cell }) => {
                const { recipient } = cell.row.original;
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{recipient && isDependant(recipient) ? `${getReadableRelationshipType(recipient)} of ${recipient.employee?.fullName}` : 'Employee'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Skipped At',
              accessorKey: 'skippedAt',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{readableDate(cell.row.original.skippedAt, 'do MMM, yyyy hh:mmaaa')}</span>
                  </div>
                );
              }
            }
          ]}
        />
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <div className={styleClassNames.floatingActions}>
          <ExtendedFAB label={`Unskip ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='exit_to_app' loading={unskipLoading} onClick={onUnskipEventsClick} variant='tertiary' />
        </div>
      )}
    </TablePage>
  );
};
